// Team email invitations (/v1/teams/{id}/invites and /v1/invites/*). An invite
// is a grant to a mailbox: a maintainer+ sends one, the recipient opens the
// emailed link and accepts it while signed in as that same address. Management
// (create/list/revoke) is authed and rides http.ts; lookup is deliberately
// anonymous — it powers the accept page BEFORE sign-in — so it uses a local
// plain-fetch helper instead (same reasoning as passkeys.ts's login pair).
// There is no separate "resend" route: re-creating an invite for the same
// address replaces the pending row and mails a fresh token, so the UI's Resend
// button is just createInvite again.

import { getJSON, sendJSON, request } from "./http.js";
import { parseError } from "./errors.js";
import type { Role } from "./teams.js";

const TEAMS = "/v1/teams";

/** Invite is one pending invitation. Rows disappear on accept or revoke. */
export interface Invite {
  id: string;
  email: string;
  role: Role;
  /** RFC3339-ish timestamp; the invite is dead after this. */
  expires: string;
  created: string;
}

/**
 * InviteLookup is the pre-login preview of an invite: enough to tell the
 * recipient what they are being offered without leaking the team's id or the
 * full invited address to whoever holds the link.
 */
export interface InviteLookup {
  teamDisplayName: string;
  role: Role;
  /** The invited address, partially masked (e.g. "a••••@example.com"). */
  emailMasked: string;
  expires: string;
}

/** AcceptedInvite is the accept result: where the caller now belongs. */
export interface AcceptedInvite {
  teamId: string;
  role: Role;
}

/**
 * createInvite invites an email address to the team (maintainer+; owner to
 * invite an owner) and mails the link. Re-inviting an address that already has
 * a pending invite replaces it — the previous link stops working.
 */
export function createInvite(teamId: string, email: string, role: Role): Promise<Invite> {
  return sendJSON<Invite>(`${TEAMS}/${encodeURIComponent(teamId)}/invites`, "POST", {
    email,
    role,
  });
}

/** listInvites returns the team's pending invitations (maintainer+). */
export async function listInvites(teamId: string): Promise<Invite[]> {
  const { items } = await getJSON<{ items: Invite[] }>(
    `${TEAMS}/${encodeURIComponent(teamId)}/invites`,
  );
  return items;
}

/** revokeInvite cancels a pending invitation, killing its link. */
export async function revokeInvite(teamId: string, inviteId: string): Promise<void> {
  await request(
    `${TEAMS}/${encodeURIComponent(teamId)}/invites/${encodeURIComponent(inviteId)}`,
    { method: "DELETE" },
  );
}

/**
 * lookupInvite previews an invite token. Anonymous by design: the accept page
 * has to render the team name and role for a signed-out visitor who followed
 * the link from their mailbox. Fails with invalid_token (404) or
 * invite_expired (410).
 */
export function lookupInvite(token: string): Promise<InviteLookup> {
  return anonJSON<InviteLookup>("/v1/invites/lookup", { token });
}

/**
 * acceptInvite redeems the token for the signed-in account and returns the
 * team just joined. The invited address must match the caller's own
 * (email_mismatch, 403) — the invite grants membership to a mailbox, not to
 * whoever happens to hold the link.
 */
export function acceptInvite(token: string): Promise<AcceptedInvite> {
  return sendJSON<AcceptedInvite>("/v1/invites/accept", "POST", { token });
}

/**
 * anonJSON POSTs a JSON body with NO Authorization header. Attaching the
 * stored token to the lookup would be worse than useless: the route is
 * anonymous, and an expired token could turn a perfectly good invite preview
 * into a confusing 401. Errors are normalized to the canonical ApiError shape
 * so callers handle them identically to http.ts failures.
 */
async function anonJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = undefined;
    }
    throw parseError({ status: res.status, response: parsed });
  }
  return (await res.json()) as T;
}
